'use client';

import { useRef, useState } from 'react';

/**
 * One box per digit of a verification code.
 *
 * 🚨 `length` is NOT cosmetic. Supabase's own OTP length is a dashboard
 * setting with no representation in this repo, and a box count that disagrees
 * with it makes the code unenterable — see `SIGNUP_CODE_LENGTH`. Callers pass
 * the constant; they do not pick a number.
 *
 * Lifted from `PairWhatsAppStep`, which is the version that had the paste and
 * fast-typing bugs beaten out of it. There are now three hand-written copies of
 * this in the tree (`PairWhatsAppStep`, `WhatsAppStep`, and the single-input
 * `WhatsAppTab`); this is the primitive so signup does not become a fourth.
 * Migrating the other three is worth doing and is deliberately not done here —
 * they each carry stage machines this component knows nothing about.
 *
 * The two comments below are the whole reason this is shared rather than
 * retyped: both describe faults that are invisible in review and only show up
 * under a real thumb on a real phone.
 */
export default function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled = false,
  label = 'Verification code',
}: {
  /** The current code, 0..length digits. Controlled. */
  value: string;
  onChange: (next: string) => void;
  /** Fired once the last digit lands — nobody should reach for a button. */
  onComplete: (code: string) => void;
  /** How many digits the sender actually sends. */
  length?: number;
  disabled?: boolean;
  label?: string;
}) {
  const boxRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [focusIdx, setFocusIdx] = useState<number | null>(null);
  const digits = Array(length)
    .fill('')
    .map((_, i) => value[i] ?? '');

  const handleDigit = (i: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, '');
    if (!cleaned) {
      onChange(digits.map((d, idx) => (idx === i ? '' : d)).join(''));
      return;
    }
    // Paste and OTP autofill drop the whole code into whichever box has focus.
    if (cleaned.length > 1) {
      const spread = cleaned.slice(0, length).split('');
      const next = Array(length)
        .fill('')
        .map((_, idx) => spread[idx] ?? '')
        .join('');
      onChange(next);
      // 🚨 Length is the completeness test. Do NOT reach for
      // `!next.includes('')` — every string contains the empty string, so that
      // guard can never pass and the code would never submit.
      if (next.length === length) onComplete(next);
      else boxRefs.current[Math.min(spread.length, length - 1)]?.focus();
      return;
    }
    const next = digits.map((d, idx) => (idx === i ? cleaned : d)).join('');
    onChange(next);
    // 🚨 Deferred a tick. Fast typing fires these back to back and the parent's
    // state has not committed yet; submitting inline hands the verifier a
    // five-digit code and the sixth keystroke silently does nothing.
    if (next.length === length) setTimeout(() => onComplete(next), 0);
    if (i < length - 1) boxRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      boxRefs.current[i - 1]?.focus();
      onChange(digits.map((d, idx) => (idx === i - 1 ? '' : d)).join(''));
    }
  };

  return (
    // 🚨 `--otp-count` drives the column count, the gap and the font size.
    // Without it the grid falls back to six columns and a longer code wraps
    // onto a second row — which is exactly how the eight-digit signup code
    // shipped on 15 September.
    <div
      className="mucka-onb-wa-code"
      role="group"
      aria-label={label}
      style={{ '--otp-count': length } as React.CSSProperties}
    >
      {digits.map((d, i) => (
        // The cell's chamfered background IS the border ring — a clipped
        // box-shadow cannot trace the octagon, so the box sits inset inside it
        // and the ring shows through.
        <span key={i} className={`mucka-onb-wa-code-cell${focusIdx === i ? ' is-focused' : ''}`}>
          <input
            ref={(el) => {
              boxRefs.current[i] = el;
            }}
            className="mucka-onb-wa-code-box"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d*"
            maxLength={length}
            value={d}
            disabled={disabled}
            onChange={(e) => handleDigit(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={(e) => {
              e.target.select();
              setFocusIdx(i);
            }}
            onBlur={() => setFocusIdx((c) => (c === i ? null : c))}
            aria-label={`Digit ${i + 1}`}
          />
        </span>
      ))}
    </div>
  );
}
